import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadOpenCV, locate, renderPage, toCanvas } from "@/lib/scanner/cv";
import { pagesToPdf } from "@/lib/scanner/pdf";
import { ScanReview } from "./ScanReview";
import { CornerEditor } from "./CornerEditor";

const MAX_SIDE = 2600;

const fileToCanvas = async (file) => {
  const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  const c = toCanvas(bmp, bmp.width, bmp.height, MAX_SIDE);
  bmp.close?.();
  return c;
};

// Native camera input (capture) + gallery input. Must be clicked from a user tap.
export const PhotoInputs = ({ cameraRef, galleryRef, onFiles }) => {
  const pick = (fromCamera) => (e) => { const fs = [...(e.target.files || [])]; e.target.value = ""; if (fs.length) onFiles(fs, fromCamera); };
  return (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick(true)} data-testid="scanner-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={pick(false)} data-testid="scanner-photo-input" />
    </>
  );
};

// Phone camera photo -> corner crop -> clean-up -> multi-page PDF. onSave(file: PDF) must return a promise.
export const DocScanner = ({ open, initialFiles, loadNumber, onClose, onSave, prefix = "POD" }) => {
  const [cv, setCv] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [pages, setPages] = useState([]);
  const [inbox, setInbox] = useState([]); // photos waiting for OpenCV / decoding
  const [crops, setCrops] = useState([]);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPages([]); setCrops([]); setReplaceIdx(null); setLoadError(""); setDone(false); setEditIdx(null);
    setInbox((initialFiles || []).map((f) => ({ file: f, replaceIdx: null })));
    loadOpenCV().then(setCv, (e) => setLoadError(e.message));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Decode + find edges for each new photo once OpenCV is ready, then queue it for the corner editor.
  useEffect(() => {
    if (!cv || !inbox.length) return;
    const [item, ...rest] = inbox;
    setInbox(rest);
    fileToCanvas(item.file).then((raw) => {
      setCrops((cs) => [...cs, { id: crypto.randomUUID(), raw, ...locate(cv, raw), replaceIdx: item.replaceIdx }]);
    }).catch(() => {});
  }, [cv, inbox]);

  // Background clean-up of confirmed crops, one page at a time.
  const nextId = pages.find((p) => !p.scan)?.id;
  useEffect(() => {
    if (!cv || !nextId) return undefined;
    const t = setTimeout(() => {
      setPages((ps) => ps.map((p) => (p.id === nextId ? { ...p, ...renderPage(cv, p.raw, p.quad) } : p)));
    }, 60);
    return () => clearTimeout(t);
  }, [cv, nextId]);

  if (!open) return null;

  const addFiles = (files, idx = replaceIdx) => {
    setInbox((ib) => [...ib, ...files.map((file, i) => ({ file, replaceIdx: i === 0 ? idx : null }))]);
    setReplaceIdx(null);
  };
  const openCamera = (idx = null) => { setReplaceIdx(idx); cameraRef.current?.click(); };
  const inputs = <PhotoInputs cameraRef={cameraRef} galleryRef={galleryRef} onFiles={(fs, fromCamera) => addFiles(fs, fromCamera ? replaceIdx : null)} />;

  if (!cv) {
    return (
      <div className="fixed inset-0 z-50 bg-black text-white grid place-items-center p-8 text-center" data-testid="scanner-loading">
        {inputs}
        <button onClick={onClose} className="absolute top-3 left-3 p-2 rounded-full hover:bg-white/10" aria-label="Close" data-testid="scanner-loading-close-btn"><X className="w-6 h-6" /></button>
        {loadError ? (
          <div className="space-y-4">
            <p>{loadError}. Check your signal and try again.</p>
            <Button onClick={() => { setLoadError(""); loadOpenCV().then(setCv, (e) => setLoadError(e.message)); }} data-testid="scanner-retry-btn">Try again</Button>
          </div>
        ) : (
          <div className="space-y-3"><Loader2 className="w-8 h-8 mx-auto animate-spin" /><p className="text-sm text-white/80">Preparing scanner… (first time takes a few seconds)</p></div>
        )}
      </div>
    );
  }

  const confirmCrop = (quad) => {
    const c = crops[0];
    const p = { id: crypto.randomUUID(), raw: c.raw, quad, detected: true, mode: "scan", scan: null, color: null };
    setCrops((cs) => cs.slice(1));
    if (c.replaceIdx !== null) setPages((ps) => ps.map((x, i) => (i === c.replaceIdx ? p : x)));
    else setPages((ps) => [...ps, p]);
  };
  const dropCrop = () => setCrops((cs) => cs.slice(1));

  const save = async () => {
    setSaving(true);
    try {
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      await onSave(await pagesToPdf(pages, `${prefix}-${loadNumber}-${stamp}.pdf`));
      setDone(true);
      setTimeout(onClose, 1300);
    } catch {
      // keep the scanner open so captured pages are not lost
    } finally {
      setSaving(false);
    }
  };

  const close = () => { if (!pages.length || window.confirm("Discard the scanned pages?")) onClose(); };
  const crop = crops[0];
  const on = {
    mode: (i, mode) => setPages((ps) => ps.map((x, j) => (j === i ? { ...x, mode } : x))),
    retake: (i) => openCamera(i),
    remove: (i) => setPages((ps) => ps.filter((_, j) => j !== i)),
    adjust: (i) => setEditIdx(i),
    move: (i, d) => setPages((ps) => { const n = [...ps]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; }),
  };

  return (
    <>
      {inputs}
      <ScanReview pages={pages} saving={saving} done={done} on={on} reading={inbox.length > 0}
        onAddPage={() => openCamera()} onGallery={() => { setReplaceIdx(null); galleryRef.current?.click(); }} onSave={save} onBack={close} />
      {crop && (
        <CornerEditor key={crop.id} cv={cv} page={crop} mustAdjust={!crop.detected}
          onDone={confirmCrop} onCancel={dropCrop} onRetake={() => { dropCrop(); openCamera(crop.replaceIdx); }} />
      )}
      {!crop && editIdx !== null && pages[editIdx] && (
        <CornerEditor cv={cv} page={pages[editIdx]} onCancel={() => setEditIdx(null)} onRetake={() => { const i = editIdx; setEditIdx(null); openCamera(i); }}
          onDone={(quad) => { setPages((ps) => ps.map((x, j) => (j === editIdx ? { ...x, quad, scan: null, color: null } : x))); setEditIdx(null); }} />
      )}
    </>
  );
};
